import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { createUser, fetchRoleGroups } from '../../services/api';
import './UserRegisterPage.css';

function UserRegisterPage() {
    const navigate = useNavigate();
    const [roleGroups, setRoleGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    // 권한 그룹 목록 API에서 가져오기
    useEffect(() => {
        const loadRoleGroups = async () => {
            try {
                setLoading(true);
                const data = await fetchRoleGroups();
                
                setRoleGroups(data);
            } catch (err) {
                console.error('권한 그룹 목록 조회 실패:', err);
                alert('권한 그룹 목록을 불러오는데 실패했습니다.');
            } finally {
                setLoading(false);
            }
        };

        loadRoleGroups();
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        position: '',
        roleGroupId: '', // 기본값: 빈 값 (첫 번째 권한 그룹으로 설정됨)
        department: '',
        status: '활성'
    });

    // 권한 그룹이 로드되면 기본값 설정
    useEffect(() => {
        if (roleGroups.length > 0 && !formData.roleGroupId) {
            setFormData(prev => ({
                ...prev,
                roleGroupId: roleGroups[0].id
            }));
        }
    }, [roleGroups]);

    // 선택된 권한 그룹 가져오기
    const selectedRoleGroup = roleGroups.find(rg => rg.id === parseInt(formData.roleGroupId));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const unmapStatus = (displayStatus) => {
        const statusMap = {
            '활성': 'ACTIVE',
            '비활성': 'INACTIVE',
            '잠김': 'LOCKED'
        };
        return statusMap[displayStatus] || 'ACTIVE';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 비밀번호 확인 검증
        if (formData.password !== formData.confirmPassword) {
            alert('비밀번호가 일치하지 않습니다.');
            return;
        }

        try {
            const userData = {
                email: formData.email,
                password: formData.password,
                name: formData.name,
                phone: formData.phone || null,
                department: formData.department || null,
                position: formData.position || null,
                roleGroupId: parseInt(formData.roleGroupId),
                status: unmapStatus(formData.status)
            };

            await createUser(userData);
            alert('사용자가 등록되었습니다.');
            navigate('/user');
        } catch (error) {
            console.error('Failed to create user:', error);
            alert('사용자 등록에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const handleCancel = () => {
        navigate('/user');
    };

    return (
        <MainLayout>
            <div className="user-register-container">
                {/* Header Section */}
                <div className="user-register-header">
                    <div className="user-register-header-left">
                        <div className="user-register-title">사용자 등록</div>
                        <p className="user-register-subtitle">새로운 사용자 계정 생성</p>
                    </div>
                </div>

                {/* Form Section */}
                <div className="user-register-content">
                    <form onSubmit={handleSubmit}>
                        {/* 기본 정보 섹션 */}
                        <div className="form-section">
                            <h3 className="section-title">기본 정보</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">
                                        이름 <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-input"
                                        placeholder="사용자 이름 입력"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        이메일 <span className="required">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        className="form-input"
                                        placeholder="example@company.com"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        autoComplete="off"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        비밀번호 <span className="required">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="password"
                                        className="form-input"
                                        placeholder="비밀번호 입력 (8자 이상)"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        autoComplete="new-password"
                                        required
                                        minLength={8}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        비밀번호 확인 <span className="required">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        className="form-input"
                                        placeholder="비밀번호 재입력"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        autoComplete="new-password"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 조직 정보 섹션 */}
                        <div className="form-section">
                            <h3 className="section-title">조직 정보</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">
                                        전화번호
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        className="form-input"
                                        placeholder="010-0000-0000"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        부서
                                    </label>
                                    <input
                                        type="text"
                                        name="department"
                                        className="form-input"
                                        placeholder="부서명 입력"
                                        value={formData.department}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        직위
                                    </label>
                                    <input
                                        type="text"
                                        name="position"
                                        className="form-input"
                                        placeholder="직위 입력"
                                        value={formData.position}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        계정 상태 <span className="required">*</span>
                                    </label>
                                    <div className="dropdown-container">
                                        <select
                                            name="status"
                                            className="form-select"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="활성">활성</option>
                                            <option value="비활성">비활성</option>
                                        </select>
                                        <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        권한 그룹 <span className="required">*</span>
                                    </label>
                                    <div className="dropdown-container">
                                        <select
                                            name="roleGroupId"
                                            className="form-select"
                                            value={formData.roleGroupId}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {roleGroups.map(rg => (
                                                <option key={rg.id} value={rg.id}>
                                                    {rg.name} ({rg.code})
                                                </option>
                                            ))}
                                        </select>
                                        <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 권한 설정 섹션 (읽기 전용) */}
                        <div className="form-section">
                            <h3 className="section-title">
                                권한 설정
                                <span className="subtitle-note">(선택한 권한 그룹에 따라 자동 설정됨)</span>
                            </h3>
                            <div className="permissions-info">
                                {selectedRoleGroup?.permissions && selectedRoleGroup.permissions.length > 0 ? (
                                    <div className="permissions-tree">
                                        {Object.entries(
                                            selectedRoleGroup.permissions.reduce((acc, perm) => {
                                                const category = perm.category || '기타';
                                                if (!acc[category]) {
                                                    acc[category] = [];
                                                }
                                                acc[category].push(perm);
                                                return acc;
                                            }, {})
                                        ).map(([category, perms]) => (
                                            <div key={category} className="permission-category">
                                                <div className="category-header">
                                                    <span className="category-icon">📁</span>
                                                    <span className="category-name">{category}</span>
                                                    <span className="permission-count">({perms.length})</span>
                                                </div>
                                                <div className="category-permissions">
                                                    {perms.map((perm) => (
                                                        <div key={perm.id} className="permission-item granted">
                                                            <span className="permission-icon">✓</span>
                                                            <span className="permission-label">{perm.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="detail-value">권한 그룹을 선택하면 권한 정보가 표시됩니다.</div>
                                )}
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="form-actions">
                            <button type="button" className="btn-secondary" onClick={handleCancel}>
                                취소
                            </button>
                            <button type="submit" className="btn-primary">
                                사용자 등록
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </MainLayout>
    );
}

export default UserRegisterPage;
